import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { api } from '../../lib/api';
import type { Page, Site } from '../../lib/types';
import { Banner, Card, LoadError, Segmented, Skeleton } from '../../lib/ui';
import { categoryLabel, countryLabel, deviceLabel } from '../../lib/labels';
import { geoBlind } from './geoBlind';
import { Hilfe } from '../help/Help';
import { fmtDate, fmtNum, fmtPct, locale, t } from '../../lib/i18n';
import type { TopicSlug } from '../help/topics';

/**
 * The dashboard (Doc 03 §7, Doc 04 §4).
 *
 * No chart library. Every shape here is a polyline or a row of divs, which is a few dozen
 * lines against ~150 kB of dependency — and the moment a real charting need appears
 * (zoom, tooltips, brushing) it can be added without unpicking anything.
 */
interface Overview {
  range: { from: string; to: string };
  funnel: {
    pageviews: number; impressions: number; interactions: number;
    consents: number; errors: number; subjects: number;
  };
  daily: {
    day: string; total: number; accept_all: number;
    reject_all: number; save_custom: number; subjects: number;
  }[];
  hourly: { hour: string; impressions: number; consents: number }[];
  categories: { consents: number; granted: Record<string, number>; custom: Record<string, number> };
  latency: { samples: number; p50: number; p75: number; p90: number; p95: number; p99: number; avg: number };
}
interface BreakdownRow {
  key: string; impressions: number; consents: number; any_optin: number;
}

const RANGES: [number, string][] = [[7, '7 Tage'], [30, '30 Tage'], [90, '90 Tage']];

const iso = (d: Date): string => d.toISOString().slice(0, 10);
const pct = (n: number, of: number): number => (of > 0 ? (100 * n) / of : 0);


export function StatsScreen({ site }: { site: Site }) {
  const [days, setDays] = useState(30);
  const to = new Date();
  const from = new Date(Date.now() - (days - 1) * 86400000);
  const qs = `from=${iso(from)}&to=${iso(to)}`;

  const overview = useQuery({
    queryKey: ['stats', site.id, days],
    queryFn: () => api<Overview>(`/v1/sites/${site.id}/stats/overview?${qs}`),
    // Switching 7/30/90 days used to blank the whole dashboard to zeros before refilling,
    // so every range change briefly claimed the site had no traffic.
    placeholderData: keepPreviousData,
  });
  const countries = useQuery({
    queryKey: ['bd', site.id, days, 'country'],
    queryFn: () => api<Page<BreakdownRow>>(`/v1/sites/${site.id}/stats/breakdown?by=country&${qs}`),
    placeholderData: keepPreviousData,
  });
  const devices = useQuery({
    queryKey: ['bd', site.id, days, 'device'],
    queryFn: () => api<Page<BreakdownRow>>(`/v1/sites/${site.id}/stats/breakdown?by=device&${qs}`),
    placeholderData: keepPreviousData,
  });

  const d = overview.data;
  const f = d?.funnel;
  // Impressions, not pageviews, is the denominator: a returning visitor with a stored
  // decision produces a pageview and no banner, and counting those would make every
  // healthy site look like it has a terrible opt-in rate.
  const optIn = d ? d.daily.reduce((a, x) => a + (x.total - x.reject_all), 0) : 0;
  const decisions = d ? d.daily.reduce((a, x) => a + x.total, 0) : 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Segmented
          value={String(days)}
          onChange={(v) => setDays(Number(v))}
          options={RANGES.map(([n, label]) => ({ value: String(n), label: t(label) }))}
        />
        {d && (
          <span className="text-xs text-[var(--color-muted)]">
            {/* ISO dates in a German console read as a database dump. */}
            {fmtDate(d.range.from)} – {fmtDate(d.range.to)}
            {` · ${t('Bots ausgenommen')}`}
          </span>
        )}
      </div>

      {overview.isError && <LoadError what={t('Die Auswertung')} onRetry={() => void overview.refetch()} />}

      {d && decisions === 0 && (
        <Banner kind="info">
          {t('Noch keine Entscheidungen in diesem Zeitraum. Die Auswertung füllt sich, sobald Besucher das Banner sehen — die Aggregate werden beim Eintreffen gebildet, nicht rückwirkend.')}
        </Banner>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {/* Until the numbers are known they are dashes. A zero here is a claim about the
            customer's traffic, and making it while still loading is a lie with a very
            short life and a lasting impression. */}
        <Kpi label={t('Einwilligungsrate')} help="auswertung"
          value={!d ? '—' : decisions === 0 ? '—' : fmtPct(pct(optIn, decisions))} big
          hint={!d ? t('wird geladen …')
            : decisions === 0 ? t('noch keine Entscheidungen')
            : t('{optIn} von {all} Entscheidungen', { optIn: fmtNum(optIn), all: fmtNum(decisions) })} />
        <Kpi label={t('Banner gezeigt')} value={d ? fmtNum(f!.impressions) : '—'}
          hint={!d ? t('wird geladen …')
            : f!.impressions === 0 ? t('das Banner wurde noch nie ausgeliefert')
            : t('{n} unterschiedliche Besucher', { n: fmtNum(f!.subjects) })} />
        <Kpi label={t('Entscheidungsquote')}
          value={!d ? '—' : f!.impressions === 0 ? '—' : fmtPct(pct(f!.consents, f!.impressions))}
          hint={t('Anteil der gezeigten Banner mit Entscheidung')} />
        <Kpi label={t('Ø Entscheidungszeit')}
          value={d && d.latency.samples > 0 ? t('{s} s', { s: fmtSec(d.latency.p50) }) : '—'}
          hint={d && d.latency.samples > 0
            ? t('Median · p95 {s} s', { s: fmtSec(d.latency.p95) })
            : t('noch keine Messung')} />
      </div>

      <div className="grid gap-4 lg:grid-cols-[1.6fr_1fr]">
        <Card title={t('Entscheidungen pro Tag')}>
          {overview.isPending ? <Skeleton rows={6} />
            : d && d.daily.length > 0 ? <DailyChart daily={d.daily} /> : <Empty />}
        </Card>

        <Card title={t('Trichter')}>
          {f ? (
            <div className="space-y-2">
              <FunnelBar label={t('Seitenaufrufe')} n={f.pageviews} of={f.pageviews} />
              <FunnelBar label={t('Banner gezeigt')} n={f.impressions} of={f.pageviews} />
              <FunnelBar label={t('Interaktion')} n={f.interactions} of={f.pageviews} />
              <FunnelBar label={t('Entscheidung')} n={f.consents} of={f.pageviews} />
              {f.errors > 0 && (
                <p className="pt-2 text-xs text-[var(--color-danger)]">
                  {t('{n} Fehlerereignisse im Zeitraum.', { n: fmtNum(f.errors) })}
                </p>
              )}
            </div>
          ) : <Empty />}
        </Card>

        <Card title={t('Erlaubte Kategorien')}>
          {d && d.categories.consents > 0 ? (
            <div className="space-y-2">
              {Object.entries({ ...d.categories.granted, ...d.categories.custom })
                .sort(([, a], [, b]) => b - a)
                .map(([k, v]) => (
                  <FunnelBar key={k} label={categoryLabel(k)} n={v} of={d.categories.consents} />
                ))}
              <p className="pt-1 text-xs text-[var(--color-muted)]">
                {t('Anteil der Entscheidungen, in denen die Kategorie erlaubt wurde.')}
              </p>
            </div>
          ) : <Empty />}
        </Card>

        <div className="grid gap-4 sm:grid-cols-2">
          <Card title={t('Länder')}>
            {countries.isError
              ? <LoadError what={t('Die Länderverteilung')} onRetry={() => void countries.refetch()} />
              : countries.isPending ? <Skeleton rows={5} />
              : geoBlind(countries.data.data)
                ? <NoCountries rows={countries.data.data} />
                : <Ranked rows={countries.data.data} label={countryLabel} />}
          </Card>
          <Card title={t('Geräte')}>
            {devices.isError
              ? <LoadError what={t('Die Geräteverteilung')} onRetry={() => void devices.refetch()} />
              : devices.isPending ? <Skeleton rows={3} />
              : <Ranked rows={devices.data.data} label={deviceLabel} />}
          </Card>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, hint, big, help }: {
  label: string; value: string; hint: string; big?: boolean; help?: TopicSlug;
}) {
  return (
    <div className="rounded-[4px] border border-[var(--color-line)] bg-[var(--color-surface)] p-4">
      <div className="eyebrow flex items-center">
        {label}
        {help && <Hilfe to={help} />}
      </div>
      <div className={`tabular mt-1 font-semibold ${big ? 'text-[28px] text-[var(--color-brand)]' : 'text-[22px]'}`}>
        {value}
      </div>
      <div className="mt-0.5 text-xs text-[var(--color-muted)]">{hint}</div>
    </div>
  );
}

const Empty = () => (
  <p className="py-6 text-center text-sm text-[var(--color-muted)]">{t('Keine Daten im Zeitraum.')}</p>
);

/** One decimal, with the separator the locale uses — not a hardcoded German comma. */
const fmtSec = (ms: number): string =>
  (ms / 1000).toLocaleString(locale(), { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/**
 * Stacked bars: rejected below, individually saved in the middle, accepted on top.
 *
 * Stacked rather than three lines because the three add up to the whole, and a reader
 * should be able to see "what happened to today's decisions" without adding numbers.
 */
function DailyChart({ daily }: { daily: Overview['daily'] }) {
  const max = Math.max(1, ...daily.map((d) => d.total));
  const w = 100 / daily.length;
  return (
    <div>
      <svg viewBox="0 0 100 42" preserveAspectRatio="none" className="h-40 w-full" role="img"
        aria-label={t('Entscheidungen pro Tag, gestapelt nach Art')}>
        {daily.map((d, i) => {
          const x = i * w;
          const h = (n: number) => (n / max) * 40;
          const acc = h(d.accept_all), sav = h(d.save_custom), rej = h(d.reject_all);
          return (
            <g key={d.day} className="group">
              <title>{dayTitle(d)}</title>
              {/* The hover target is the whole column, not the drawn bar: across 90 days a
                  bar is a few pixels wide, and a day with no decisions has no height to
                  aim at. The band under the bars is what says which day is being read. */}
              <rect x={x} width={w} y="0" height="42"
                className="fill-transparent group-hover:fill-[var(--color-sunken)]" />
              <rect x={x + w * 0.15} width={w * 0.7} y={41 - rej} height={rej} fill="var(--color-muted)" opacity=".45" />
              <rect x={x + w * 0.15} width={w * 0.7} y={41 - rej - sav} height={sav} fill="var(--color-brand)" opacity=".45" />
              <rect x={x + w * 0.15} width={w * 0.7} y={41 - rej - sav - acc} height={acc} fill="var(--color-brand)" />
            </g>
          );
        })}
      </svg>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[var(--color-muted)]">
        <Legend color="var(--color-brand)" label={t('Alle akzeptiert')} />
        <Legend color="var(--color-brand)" label={t('Individuell gespeichert')} faint />
        <Legend color="var(--color-muted)" label={t('Alle abgelehnt')} faint />
        <span className="ml-auto tabular">{t('Spitze: {n}/Tag', { n: fmtNum(max) })}</span>
      </div>
    </div>
  );
}

/**
 * What one day's bar is made of, for the hover.
 *
 * The bar shows the split by height; the numbers behind it were only in the API. Shares
 * are of that day's decisions, so they answer "what did this day do" rather than "how big
 * was this day" — which the height already says. A day with nothing in it gets the head
 * line alone instead of three zeroes.
 */
function dayTitle(d: Overview['daily'][number]): string {
  const head = t('{day}: {n} Entscheidungen', { day: fmtDate(d.day), n: fmtNum(d.total) });
  if (d.total === 0) return head;
  const row = (label: string, n: number): string =>
    `${label}: ${fmtNum(n)} \u00b7 ${fmtPct(pct(n, d.total))}`;
  return [
    head,
    row(t('Alle akzeptiert'), d.accept_all),
    row(t('Individuell gespeichert'), d.save_custom),
    row(t('Alle abgelehnt'), d.reject_all),
  ].join('\n');
}

const Legend = ({ color, label, faint }: { color: string; label: string; faint?: boolean }) => (
  <span className="flex items-center gap-1.5">
    <span className="inline-block h-2 w-2 rounded-[1px]"
      style={{ background: color, opacity: faint ? 0.45 : 1 }} />
    {label}
  </span>
);

function FunnelBar({ label, n, of }: { label: string; n: number; of: number }) {
  const p = pct(n, of);
  return (
    <div>
      <div className="flex items-baseline justify-between text-[13px]">
        <span>{label}</span>
        <span className="tabular text-xs text-[var(--color-muted)]">
          {fmtNum(n)} · {fmtPct(p)}
        </span>
      </div>
      <div className="mt-1 h-1.5 overflow-hidden rounded-[1px] bg-[var(--color-sunken)]">
        <div className="h-full bg-[var(--color-brand)]" style={{ width: `${Math.min(100, p)}%` }} />
      </div>
    </div>
  );
}

/**
 * What to show instead of one full-width bar labelled "Unbekannt".
 *
 * The country is read from the proxy in front of the ingest and never from the browser
 * (Doc 00 §5) — a visitor who could name their own country could pick the mildest banner.
 * No deployment in this repo puts such a header there: GeoIP is listed among the pieces
 * deliberately not wired up yet in docker-compose.prod.yml, so unless Cloudflare or an
 * nginx with the geoip module sits in front, every event lands in this single bucket.
 * Drawing that as a chart makes a missing input look like a measurement.
 */
function NoCountries({ rows }: { rows: BreakdownRow[] }) {
  const n = rows.reduce((a, r) => a + r.impressions, 0);
  return (
    <div className="space-y-2 py-1">
      <p className="text-[13px]">
        {t('Für alle {n} Auslieferungen ist kein Land bekannt.', { n: fmtNum(n) })}
      </p>
      <p className="text-xs leading-relaxed text-[var(--color-muted)]">
        {t('Das Land kommt aus dem Header des Proxys vor dem Ingest (CF-IPCountry oder X-Geo-Country), nie aus dem Browser. Fehlt der Header, bleibt diese Verteilung leer — und für die Rechtslage gilt das Rückfall-Regime des Kontos, also die strengere Annahme.')}
      </p>
    </div>
  );
}

function Ranked({ rows, label }: { rows: BreakdownRow[]; label: (k: string) => string }) {
  if (rows.length === 0) return <Empty />;
  const max = Math.max(1, ...rows.map((r) => r.impressions));
  return (
    <ul className="space-y-1.5">
      {rows.map((r) => (
        <li key={r.key}>
          <div className="flex items-baseline justify-between text-[13px]">
            <span className="text-[13px]">{label(r.key)}</span>
            <span className="tabular text-xs text-[var(--color-muted)]">
              {fmtNum(r.impressions)}
              {r.consents > 0 && ` \u00b7 ${t('{pct} Einwilligung', { pct: fmtPct(pct(r.any_optin, r.consents)) })}`}
            </span>
          </div>
          <div className="mt-0.5 h-1 overflow-hidden rounded-[1px] bg-[var(--color-sunken)]">
            <div className="h-full bg-[var(--color-brand)] opacity-70"
              style={{ width: `${(100 * r.impressions) / max}%` }} />
          </div>
        </li>
      ))}
    </ul>
  );
}
